import { type CSSProperties, type RefObject, useLayoutEffect, useState } from 'react'

import { cn } from '@/lib/utils'

/** Clears the sticky site header for the mobile services stack. */
export const SITE_HEADER_CLEARANCE_REM = 5

/**
 * Tailwind overrides merged after preset bar classes while scrollY ≤ threshold.
 * Hides border, background, and glass chrome until the user scrolls.
 */
export const stickyHeaderBarRestingClassName =
  '!border-transparent !bg-transparent !shadow-none !ring-0 !backdrop-blur-none !backdrop-saturate-100 supports-[backdrop-filter]:!bg-transparent dark:!border-transparent dark:!bg-transparent dark:!shadow-none dark:!ring-0 dark:supports-[backdrop-filter]:!bg-transparent before:!opacity-0 transition-[background-color,backdrop-filter,box-shadow,border-color,ring-color,opacity] duration-300 ease-out'

/** Inline fallback — wins over preset bg/backdrop when utilities still collide. */
export const stickyHeaderBarRestingStyle = {
  backgroundColor: 'transparent',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  boxShadow: 'none',
  borderColor: 'transparent',
} as const

const stickyHeaderBarScrolledBaseClassName =
  '!shadow-none !ring-0 !backdrop-blur-none !backdrop-saturate-100 before:!opacity-0 transition-[background-color,backdrop-filter,box-shadow,border-color,ring-color,opacity] duration-300 ease-out'

const stickyHeaderBarScrolledBaseStyle = {
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  boxShadow: 'none',
} as const satisfies Pick<
  CSSProperties,
  'backdropFilter' | 'WebkitBackdropFilter' | 'boxShadow'
>

export type StickyHeaderScrolledScheme = {
  barClassName: string
  barStyle: CSSProperties
  navClassName: string
  navLinkClassName: string
  logoClassName: string
  logoWordmarkClassName: string
  /** Overrides the default orange logomark stroke (e.g. white on ember). */
  logoMarkClassName?: string
  utilityButtonClassName: string
  utilityIconClassName: string
  ctaClassName: string
}

/** Shared frosted pill surface for scrolled utility controls on dark bars. */
const stickyHeaderUtilitySurfaceClassName =
  'rounded-xl border border-white/20 bg-white/10 text-cream shadow-none backdrop-blur-none hover:bg-white/15 hover:text-white focus-visible:ring-3 focus-visible:ring-white/25 focus-visible:outline-none aria-expanded:bg-white/15 aria-expanded:text-white dark:border-white/15 dark:bg-white/10 dark:text-white dark:hover:text-white'

const stickyHeaderUtilityButtonClassName = cn(
  'inline-flex size-9 shrink-0 items-center justify-center',
  stickyHeaderUtilitySurfaceClassName,
)

const stickyHeaderUtilityCtaClassName = cn(
  'inline-flex h-9 shrink-0 items-center justify-center px-4 text-sm font-medium',
  stickyHeaderUtilitySurfaceClassName,
)

const stickyHeaderDarkTypeClasses = {
  navClassName: 'text-cream dark:text-white',
  navLinkClassName: 'hover:bg-white/10 hover:text-white',
  logoClassName: 'text-cream hover:text-white dark:text-white dark:hover:text-white',
  logoWordmarkClassName: 'text-cream dark:text-white',
  utilityButtonClassName: stickyHeaderUtilityButtonClassName,
  utilityIconClassName: 'opacity-90 invert',
  ctaClassName: stickyHeaderUtilityCtaClassName,
} as const satisfies Omit<
  StickyHeaderScrolledScheme,
  'barClassName' | 'barStyle' | 'logoMarkClassName'
>

function solidScrolledScheme(
  backgroundClassName: string,
  extras?: Pick<StickyHeaderScrolledScheme, 'logoMarkClassName'>,
): StickyHeaderScrolledScheme {
  return {
    barClassName: cn(
      stickyHeaderBarScrolledBaseClassName,
      '!border-white/15',
      backgroundClassName,
    ),
    barStyle: {
      ...stickyHeaderBarScrolledBaseStyle,
      // Background comes from utilities so `dark:` variants can win.
      borderColor: 'color-mix(in oklab, white 15%, transparent)',
    },
    ...stickyHeaderDarkTypeClasses,
    ...extras,
  }
}

/** Solid greyblue scrolled navbar — cream type, frosted utility pills. */
export const stickyHeaderScrolledScheme: StickyHeaderScrolledScheme =
  solidScrolledScheme(
    '!bg-greyblue supports-[backdrop-filter]:!bg-greyblue dark:!border-white/15 dark:!bg-ocean-deep dark:supports-[backdrop-filter]:!bg-ocean-deep',
  )

/** Ember bar once the sticky header reaches the logo carousel — white logomark. */
export const stickyHeaderReachedScheme: StickyHeaderScrolledScheme =
  solidScrolledScheme(
    '!bg-ember supports-[backdrop-filter]:!bg-ember dark:!border-white/15 dark:!bg-ember-deep dark:supports-[backdrop-filter]:!bg-ember-deep',
    { logoMarkClassName: 'text-white' },
  )

/** Element id on the technology logos strip — reach threshold for the ember bar. */
export const stickyHeaderReachTargetId = 'technology-logos'

export function useHasScrolled(threshold = 0) {
  const [scrolled, setScrolled] = useState(false)

  useLayoutEffect(() => {
    const update = () => {
      setScrolled(window.scrollY >= threshold)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [threshold])

  return scrolled
}

export type HeaderScrollPhase = {
  /** True only at scrollY === 0 — keeps the initial top inset. */
  atTop: boolean
  /** True once scrollY reaches the header’s top inset — drives background chrome. */
  showBackground: boolean
  /**
   * True once the sticky bar reaches the logo carousel (or configured target).
   * Drives the secondary (ember) surface.
   */
  reachedSurface: boolean
}

type UseHeaderScrollPhaseOptions = {
  /** DOM id of the section that triggers the reached surface (logo carousel). */
  reachTargetId?: string
  /** Sticky bar element — its bottom edge is compared to the reach target top. */
  barRef?: RefObject<HTMLElement | null>
}

/** Tracks inset, background chrome, and logo-carousel reach for the sticky header. */
export function useHeaderScrollPhase(
  insetPx: number,
  options: UseHeaderScrollPhaseOptions = {},
): HeaderScrollPhase {
  const { reachTargetId, barRef } = options
  const [phase, setPhase] = useState<HeaderScrollPhase>({
    atTop: true,
    showBackground: false,
    reachedSurface: false,
  })

  useLayoutEffect(() => {
    let rafId = 0
    let hashRaf1 = 0
    let hashRaf2 = 0
    let ro: ResizeObserver | null = null
    let mo: MutationObserver | null = null

    const readReachedSurface = () => {
      if (!reachTargetId) return false
      const target = document.getElementById(reachTargetId)
      if (!target) return false
      const barHeight = barRef?.current?.offsetHeight ?? 0
      return target.getBoundingClientRect().top <= barHeight
    }

    const updateChrome = () => {
      const y = window.scrollY
      const showBackground = insetPx > 0 ? y >= insetPx : y > 0
      const atTop = y === 0
      const reachedSurface = readReachedSurface()
      setPhase((prev) => {
        if (
          prev.atTop === atTop &&
          prev.showBackground === showBackground &&
          prev.reachedSurface === reachedSurface
        ) {
          return prev
        }
        return { atTop, showBackground, reachedSurface }
      })
    }

    const scheduleUpdate = () => {
      if (rafId) return
      rafId = window.requestAnimationFrame(() => {
        rafId = 0
        updateChrome()
      })
    }

    // Instant hash jumps often omit `scroll` on mobile, and the browser may
    // apply the offset after hashchange — re-read now and on the next frames.
    const syncAfterHash = () => {
      updateChrome()
      hashRaf1 = window.requestAnimationFrame(() => {
        updateChrome()
        hashRaf2 = window.requestAnimationFrame(updateChrome)
      })
    }

    const observeLayout = () => {
      ro?.disconnect()
      ro = new ResizeObserver(scheduleUpdate)
      const bar = barRef?.current
      const target = reachTargetId
        ? document.getElementById(reachTargetId)
        : null
      if (bar) ro.observe(bar)
      if (target) ro.observe(target)
      return Boolean(target) || !reachTargetId
    }

    updateChrome()
    window.addEventListener('scroll', updateChrome, { passive: true })
    window.addEventListener('hashchange', syncAfterHash)
    window.addEventListener('popstate', syncAfterHash)
    window.addEventListener('pageshow', scheduleUpdate)
    window.addEventListener('resize', scheduleUpdate)

    if (!observeLayout() && reachTargetId) {
      mo = new MutationObserver(() => {
        if (!observeLayout()) return
        mo?.disconnect()
        mo = null
        // Lazy sections just mounted — the document may jump to the current hash.
        syncAfterHash()
      })
      mo.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      window.removeEventListener('scroll', updateChrome)
      window.removeEventListener('hashchange', syncAfterHash)
      window.removeEventListener('popstate', syncAfterHash)
      window.removeEventListener('pageshow', scheduleUpdate)
      window.removeEventListener('resize', scheduleUpdate)
      if (rafId) window.cancelAnimationFrame(rafId)
      if (hashRaf1) window.cancelAnimationFrame(hashRaf1)
      if (hashRaf2) window.cancelAnimationFrame(hashRaf2)
      ro?.disconnect()
      mo?.disconnect()
    }
  }, [insetPx, reachTargetId, barRef])

  return phase
}

/** Reads an element’s computed padding-top — used as the header background scroll threshold. */
export function useElementPaddingTop(ref: RefObject<HTMLElement | null>) {
  const [paddingTop, setPaddingTop] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const update = () => {
      setPaddingTop(parseFloat(getComputedStyle(el).paddingTop) || 0)
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [ref])

  return paddingTop
}
